import { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Switch, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Chip, Alert, CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { api } from '../../api/client.js';
import type { IQuickWin } from '../../../shared/types.js';
import { colors } from '../../styles/theme.js';

export default function QuickWinsPage() {
  const [quickWins, setQuickWins] = useState<IQuickWin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState<IQuickWin | null>(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    const res = await api.get<{ quick_wins: IQuickWin[] }>('/config/quick-wins');
    if (res.success) {
      setQuickWins(res.data.quick_wins);
      setError(null);
    } else {
      setError(res.error.message);
    }
    setLoading(false);
  };

  useEffect(() => { void loadData(); }, []);

  const openEdit = (qw: IQuickWin) => {
    setEditDialog(qw);
    setEditForm({ title: qw.title, description: qw.description ?? '' });
  };

  const handleSave = async () => {
    if (!editDialog) return;
    const res = await api.patch(`/config/quick-wins/${editDialog.id}`, editForm);
    if (res.success) {
      setEditDialog(null);
      loadData(true);
    } else {
      setError((res as { success: false; error: { message: string } }).error.message);
    }
  };

  const handleToggleActive = async (qw: IQuickWin) => {
    setQuickWins((prev) => prev.map((q) => q.id === qw.id ? { ...q, active: !q.active } : q));
    const res = await api.patch(`/config/quick-wins/${qw.id}`, { active: !qw.active });
    if (!res.success) {
      setError((res as { success: false; error: { message: string } }).error.message);
      loadData(true);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3 }}>Quick-Win Bibliothek</Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Alle Quick-Wins aus der Datenbank inklusive Trigger-Zuordnungen.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Auf der Score Card werden sie angezeigt, wenn ein Trigger-Check das konfigurierte Rating hat.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Paper elevation={0} sx={{ border: `1px solid ${colors.silver}`, position: 'relative' }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={36} />
          </Box>
        )}
        {!loading && (
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: colors.lightGrey }}>
              <TableCell>Titel</TableCell>
              <TableCell>Beschreibung</TableCell>
              <TableCell>Triggers</TableCell>
              <TableCell>Aktiv</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {quickWins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    Keine Quick-Wins in der Datenbank. Nach dem SQL-Seed oder manuellen Einträgen erscheinen sie hier.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
            quickWins.map((qw) => (
              <TableRow key={qw.id}>
                <TableCell sx={{ fontWeight: 600 }}>{qw.title}</TableCell>
                <TableCell sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {qw.description ?? '—'}
                </TableCell>
                <TableCell>
                  {qw.triggers?.length
                    ? qw.triggers.map((t) => (
                        <Chip
                          key={`${t.quick_win_id}-${t.check_id}`}
                          label={`${t.check_id} → ${t.trigger_rating}`}
                          size="small"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))
                    : '—'}
                </TableCell>
                <TableCell>
                  <Switch checked={qw.active} onChange={() => handleToggleActive(qw)} />
                </TableCell>
                <TableCell>
                  <IconButton size="small" onClick={() => openEdit(qw)}><EditIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))
            )}
          </TableBody>
        </Table>
        )}
      </Paper>

      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Quick-Win bearbeiten</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Titel" value={editForm.title}
            onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
          <TextField label="Beschreibung" multiline rows={3} value={editForm.description}
            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSave}>Speichern</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
